import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Save from '@mui/icons-material/esm/Save';
import Send from '@mui/icons-material/esm/Send';
import TaskAlt from '@mui/icons-material/esm/TaskAlt';
import FactCheck from '@mui/icons-material/esm/FactCheck';
import WbSunny from '@mui/icons-material/esm/WbSunny';
import { RootState } from '../../store';
import dfrService, { DailyFieldReport, DfrPayload } from '../../services/api/dfrService';
import inspectionsService, { InspectionRow } from '../../services/api/inspectionsService';
import { enqueueOfflineDfr, mergeById } from '../../services/offline/fieldOfflineForms';
import { Task } from '../../services/api/tasksService';
import SupervisorMonthCalendar from './SupervisorMonthCalendar';
import { loadAllProjectTasks } from './supervisorFieldData';
import { completedTaskDate, monthBounds, toLocalIsoDate, weatherGuidance } from './supervisorFieldUtils';

const weatherOptions = ['Clear', 'Partly cloudy', 'Overcast', 'Light rain', 'Heavy rain', 'Windy', 'Thunderstorm'];

const emptyForm = (projectId: string, reportDate: string): DfrPayload => ({
  projectId,
  reportDate,
  weather: '',
  temperatureC: null,
  manpowerCount: undefined,
  workSummary: '',
  delaysConstraints: '',
});

const inspectionDate = (inspection: InspectionRow): string | null =>
  (inspection.completedAt || inspection.scheduledAt || inspection.startedAt)?.slice(0, 10) ?? null;

const isNetworkFailure = (err: any) => !err?.response || err?.code === 'ERR_NETWORK';

export default function SupervisorFieldReportPage() {
  const { activeProjectId, projects, loading: projectsLoading } = useSelector((state: RootState) => state.projects);
  const currentUserId = useSelector((state: RootState) => state.auth.user?.id);
  const effectiveProjectId = activeProjectId ?? projects[0]?.id ?? null;
  const today = useMemo(() => new Date(), []);
  const [month, setMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState(toLocalIsoDate(today));
  const [reports, setReports] = useState<DailyFieldReport[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [inspections, setInspections] = useState<InspectionRow[]>([]);
  const [form, setForm] = useState<DfrPayload>(emptyForm(effectiveProjectId ?? '', selectedDate));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const projectName = projects.find((project) => project.id === effectiveProjectId)?.name ?? 'Active project';
  const supervisorReports = useMemo(
    () => reports.filter((report) => !currentUserId || report.authorUserId === currentUserId),
    [currentUserId, reports]
  );
  const selectedReport = useMemo(
    () => supervisorReports.find((report) => report.reportDate.slice(0, 10) === selectedDate) ?? null,
    [selectedDate, supervisorReports]
  );
  const editable = !selectedReport || ['draft', 'returned'].includes(selectedReport.status);

  const load = useCallback(async () => {
    if (!effectiveProjectId) return;
    const { from, to } = monthBounds(month);
    setLoading(true);
    setError(null);
    try {
      const [reportRows, taskRows, inspectionRows] = await Promise.all([
        dfrService.list({ projectId: effectiveProjectId, from, to }),
        loadAllProjectTasks(effectiveProjectId),
        inspectionsService.list({
          projectId: effectiveProjectId,
          scheduledAfter: `${from}T00:00:00.000Z`,
          scheduledBefore: `${to}T23:59:59.999Z`,
        }),
      ]);
      setReports(reportRows);
      setTasks(taskRows);
      setInspections(inspectionRows);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.response?.data?.error || 'Failed to load the field report calendar.');
    } finally {
      setLoading(false);
    }
  }, [effectiveProjectId, month]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!effectiveProjectId) return;
    if (selectedReport) {
      setForm({
        projectId: effectiveProjectId,
        reportDate: selectedDate,
        weather: selectedReport.weather ?? '',
        temperatureC: selectedReport.temperatureC,
        manpowerCount: selectedReport.manpowerCount || undefined,
        workSummary: selectedReport.workSummary ?? '',
        delaysConstraints: selectedReport.delaysConstraints ?? '',
      });
    } else {
      setForm(emptyForm(effectiveProjectId, selectedDate));
    }
  }, [effectiveProjectId, selectedDate, selectedReport]);

  const completedTasks = useMemo(
    () => tasks.filter((task) => completedTaskDate(task) === selectedDate),
    [selectedDate, tasks]
  );
  const selectedInspections = useMemo(
    () => inspections.filter((inspection) => inspectionDate(inspection) === selectedDate),
    [inspections, selectedDate]
  );
  const guidance = useMemo(
    () => weatherGuidance(form.weather, form.temperatureC),
    [form.temperatureC, form.weather]
  );

  const save = async (submitAfter = false) => {
    if (!effectiveProjectId || !editable) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    const derivedEntries = [
      ...completedTasks.map((task) => ({
        taskId: task.id,
        entryType: 'progress' as const,
        description: `Completed task: ${task.taskCode ? `${task.taskCode} - ` : ''}${task.title}`,
      })),
      ...selectedInspections.map((inspection) => ({
        taskId: inspection.taskId ?? null,
        inspectionId: inspection.id,
        entryType: 'inspection' as const,
        description: `Site inspection: ${inspection.inspectionNumber} - ${inspection.title} (${inspection.status})`,
      })),
    ];
    const payload = {
      ...form,
      projectId: effectiveProjectId,
      reportDate: selectedDate,
      temperatureC: form.temperatureC === null || form.temperatureC === undefined ? null : Number(form.temperatureC),
      manpowerCount: Number(form.manpowerCount ?? 0),
      entries: selectedReport ? [] : derivedEntries,
    };
    try {
      if (!navigator.onLine) throw new Error('offline');
      const saved = selectedReport
        ? await dfrService.update(selectedReport.id, payload)
        : await dfrService.create(payload);
      if (selectedReport) {
        const existingTaskEntryIds = new Set((selectedReport.entries ?? [])
          .filter((entry) => entry.entryType === 'progress')
          .map((entry) => entry.taskId)
          .filter(Boolean));
        const existingInspectionEntryIds = new Set((selectedReport.entries ?? [])
          .filter((entry) => entry.entryType === 'inspection')
          .map((entry) => entry.inspectionId)
          .filter(Boolean));
        for (const entry of derivedEntries) {
          const exists = entry.entryType === 'inspection'
            ? existingInspectionEntryIds.has(entry.inspectionId)
            : existingTaskEntryIds.has(entry.taskId);
          if (!exists) await dfrService.addEntry(saved.id, entry);
        }
      }
      if (submitAfter) await dfrService.submit(saved.id);
      setNotice(submitAfter ? 'Field report submitted.' : 'Field report saved.');
      await load();
    } catch (err: any) {
      if (!selectedReport && (!navigator.onLine || isNetworkFailure(err))) {
        const local = await enqueueOfflineDfr(payload, submitAfter);
        setReports((current) => mergeById(current, [local]));
        setNotice('Field report saved offline and will sync when the device reconnects.');
      } else {
        setError(err?.response?.data?.message || err?.response?.data?.error || 'Failed to save the field report.');
      }
    } finally {
      setSaving(false);
    }
  };

  if (!effectiveProjectId) {
    return projectsLoading
      ? <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
      : <Alert severity="warning">No project is available for field reports.</Alert>;
  }

  return (
    <Stack spacing={2.5}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
        <Box>
          <Typography variant="h4" sx={{ fontSize: 28, fontWeight: 900 }}>Field Report</Typography>
          <Typography color="text.secondary">{projectName} - weather, completed work, and site inspections</Typography>
        </Box>
        {loading && <Chip icon={<CircularProgress size={16} />} label="Refreshing" sx={{ alignSelf: 'flex-start' }} />}
      </Stack>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}

      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} lg={7}>
          <Paper variant="outlined" sx={{ p: { xs: 1, sm: 2 }, borderRadius: 1 }}>
            <SupervisorMonthCalendar
              month={month}
              selectedDate={selectedDate}
              onMonthChange={(nextMonth) => {
                setMonth(nextMonth);
                setSelectedDate(toLocalIsoDate(new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 1)));
              }}
              onSelectDate={setSelectedDate}
              renderDay={(date) => {
                const report = supervisorReports.find((row) => row.reportDate.slice(0, 10) === date);
                const completed = tasks.filter((task) => completedTaskDate(task) === date).length;
                const inspectionCount = inspections.filter((inspection) => inspectionDate(inspection) === date).length;
                return (
                  <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                    {report && <Typography noWrap sx={{ fontSize: 10, fontWeight: 800, color: '#276749' }}>{report.weather || report.status}</Typography>}
                    {completed > 0 && <Typography noWrap sx={{ fontSize: 10 }}>{completed} completed</Typography>}
                    {inspectionCount > 0 && <Typography noWrap sx={{ fontSize: 10 }}>{inspectionCount} inspection{inspectionCount === 1 ? '' : 's'}</Typography>}
                  </Stack>
                );
              }}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Box>
                  <Typography sx={{ fontSize: 18, fontWeight: 900 }}>{selectedDate}</Typography>
                  <Typography variant="body2" color="text.secondary">Weather and site analysis</Typography>
                </Box>
                {selectedReport && <Chip size="small" label={selectedReport.status} color={selectedReport.status === 'submitted' ? 'primary' : 'default'} />}
              </Stack>

              {!editable && <Alert severity="info">This report is {selectedReport?.status} and is read-only.</Alert>}

              <TextField
                select
                fullWidth
                label="Observed weather"
                value={form.weather ?? ''}
                disabled={!editable}
                onChange={(event) => setForm((current) => ({ ...current, weather: event.target.value }))}
              >
                <MenuItem value="">Not recorded</MenuItem>
                {weatherOptions.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
              </TextField>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Temperature C"
                    value={form.temperatureC ?? ''}
                    disabled={!editable}
                    onChange={(event) => setForm((current) => ({ ...current, temperatureC: event.target.value === '' ? null : Number(event.target.value) }))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Manpower"
                    value={form.manpowerCount ?? ''}
                    disabled={!editable}
                    onChange={(event) => setForm((current) => ({ ...current, manpowerCount: event.target.value === '' ? undefined : Number(event.target.value) }))}
                  />
                </Grid>
              </Grid>

              <Alert icon={<WbSunny />} severity="info">
                <Stack spacing={0.5}>
                  {guidance.map((item) => <Typography key={item} variant="body2">{item}</Typography>)}
                </Stack>
              </Alert>

              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Work summary"
                value={form.workSummary ?? ''}
                disabled={!editable}
                onChange={(event) => setForm((current) => ({ ...current, workSummary: event.target.value }))}
              />
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Weather impact, delays, and controls"
                value={form.delaysConstraints ?? ''}
                disabled={!editable}
                onChange={(event) => setForm((current) => ({ ...current, delaysConstraints: event.target.value }))}
              />

              {editable && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button fullWidth variant="outlined" startIcon={<Save />} disabled={saving} onClick={() => save(false)}>Save draft</Button>
                  <Button fullWidth variant="contained" startIcon={<Send />} disabled={saving} onClick={() => save(true)}>Submit report</Button>
                </Stack>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Divider />

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Box component="section">
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <TaskAlt color="success" />
              <Typography variant="h6" sx={{ fontWeight: 900 }}>Completed tasks</Typography>
              <Chip size="small" label={completedTasks.length} />
            </Stack>
            <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
              {completedTasks.length === 0 ? (
                <Alert severity="info">No completed tasks recorded for this date.</Alert>
              ) : completedTasks.map((task, index) => (
                <Box key={task.id} sx={{ p: 1.5, borderBottom: index === completedTasks.length - 1 ? 0 : '1px solid #e3e6e3' }}>
                  <Typography sx={{ fontWeight: 800 }}>{task.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{task.team?.name || 'Unassigned team'} - {task.workLocationRef?.name || task.workLocation || 'Site'}</Typography>
                </Box>
              ))}
            </Paper>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box component="section">
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <FactCheck color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 900 }}>Site inspections</Typography>
              <Chip size="small" label={selectedInspections.length} />
            </Stack>
            <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
              {selectedInspections.length === 0 ? (
                <Alert severity="info">No site inspections recorded for this date.</Alert>
              ) : selectedInspections.map((inspection, index) => (
                <Box key={inspection.id} sx={{ p: 1.5, borderBottom: index === selectedInspections.length - 1 ? 0 : '1px solid #e3e6e3' }}>
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 800 }} noWrap>{inspection.title}</Typography>
                      <Typography variant="body2" color="text.secondary">{inspection.inspectionNumber}</Typography>
                    </Box>
                    <Chip size="small" label={inspection.status} color={inspection.status === 'passed' ? 'success' : inspection.status === 'failed' ? 'error' : 'default'} />
                  </Stack>
                </Box>
              ))}
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </Stack>
  );
}
